import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product categories with exact order specified
const PRODUCT_CATEGORIES = [
  // Row 1
  {
    key: 'mesh',
    name: { 
      en: 'Welded Mesh', 
      es: 'Malla Electrosoldada', 
      fr: 'Treillis Soudé', 
      de: 'Geschweißte Matten', 
      nl: 'Wapeningsnetten' 
    },
    description: {
      en: 'Factory-welded steel mesh panels for reinforced concrete slabs, walls, and foundations.',
      es: 'Paneles de malla de acero electrosoldada para losas, muros y cimentaciones de hormigón armado.',
      fr: 'Panneaux de treillis d\'acier soudés pour dalles, murs et fondations en béton armé.',
      de: 'Geschweißte Stahlmatten für Stahlbetonplatten, Wände und Fundamente.',
      nl: 'Wapeningsnetten voor gewapende betonplaten, muren en funderingen.'
    },
    image: `${BACKEND_URL}/api/assets/products/welded-mesh.jpg`,
    route: '/products/welded-mesh',
    navigates: true,
    row: 1,
  },
  {
    key: 'cold-drawn',
    name: { 
      en: 'Coils', 
      es: 'Bobinas', 
      fr: 'Bobines', 
      de: 'Spulen', 
      nl: 'Koudgetrokken Draad' 
    },
    description: {
      en: 'Cold rolled wire coils for industrial applications. Wound and strapped.',
      es: 'Bobinas de alambre laminado en frío para aplicaciones industriales. Enrolladas y flejadas.',
      fr: 'Bobines de fil laminé à froid pour applications industrielles. Enroulées et cerclées.',
      de: 'Kaltgewalzte Drahtspulen für industrielle Anwendungen. Gewickelt und umreift.',
      nl: 'Koudgewalste draadrollen voor industriële toepassingen. Gewikkeld en omband.'
    },
    image: `${BACKEND_URL}/api/assets/products/coils.jpg`,
    route: '/products/cold-drawn-wire',
    navigates: true,
    row: 1,
  },
  {
    key: 'separators',
    name: { 
      en: 'Separators', 
      es: 'Separadores', 
      fr: 'Séparateurs', 
      de: 'Abstandhalter', 
      nl: 'Afstandhouders' 
    },
    description: {
      en: 'Separators and lattice girders for reinforced concrete.',
      es: 'Separadores y celosías para hormigón armado.',
      fr: 'Séparateurs et treillis pour béton armé.',
      de: 'Abstandhalter und Gitterträger für Stahlbeton.',
      nl: 'Afstandhouders en vakwerkliggers voor gewapend beton.'
    },
    image: `${BACKEND_URL}/api/assets/products/separadores.jpg`,
    route: '/products/separadores',
    navigates: true,
    row: 1,
  },
  // Row 2
  {
    key: 'wire-rod',
    name: { 
      en: 'Wire Rod', 
      es: 'Alambrón', 
      fr: 'Fil Machine', 
      de: 'Walzdraht', 
      nl: 'Walsdraad' 
    },
    description: {
      en: 'Premium wire rod for various applications including fencing, mesh production, and general industrial use.',
      es: 'Alambrón de primera calidad para diversas aplicaciones incluyendo cercas y producción de mallas.',
      fr: 'Fil machine de qualité supérieure pour diverses applications y compris les clôtures.',
      de: 'Premium Walzdraht für verschiedene Anwendungen einschließlich Zäune.',
      nl: 'Premium walsdraad voor diverse toepassingen waaronder hekwerk.'
    },
    image: `${BACKEND_URL}/api/assets/products/wire-rod.jpg`,
    route: null,
    navigates: false,
    row: 2,
  },
  {
    key: 'reinforcing',
    name: { 
      en: 'Reinforcing Steel Bars', 
      es: 'Barras', 
      fr: 'Barres d\'Acier Armé', 
      de: 'Bewehrungsstahl', 
      nl: 'Wapeningsstaal' 
    },
    description: {
      en: 'Cold rolled bars on request.',
      es: 'Barras laminadas en frío bajo pedido.',
      fr: 'Barres laminées à froid sur commande.',
      de: 'Kaltgewalzte Stäbe auf Anfrage.',
      nl: 'Koudgewalste staven op aanvraag.'
    },
    image: `${BACKEND_URL}/api/assets/products/bars.jpg`,
    route: null,
    navigates: false,
    row: 2,
  },
];

export const Products = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactProduct, setContactProduct] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const MAX_MESSAGE_LENGTH = 600;

  // Handle product click
  const handleViewDetails = (product) => {
    if (product.navigates && product.route) {
      navigate(product.route);
    } else {
      // Open contact modal for Barras and Alambrón
      setContactProduct(product);
      setShowContactModal(true);
      setFormSuccess(false);
      setFormError('');
      setContactForm({ name: '', company: '', email: '', message: '' });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        country: 'Not specified',
        phone: '',
        subject: `Product Inquiry: ${getName(contactProduct?.name)}`,
        message: contactForm.message,
        language: language
      });
      setFormSuccess(true);
    } catch (error) {
      setFormError(t('messageError') || 'Error sending message. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowContactModal(false);
    setContactProduct(null);
    setFormSuccess(false);
    setFormError('');
    setContactForm({ name: '', company: '', email: '', message: '' });
  };

  const getName = (nameObj) => nameObj?.[language] || nameObj?.['en'] || '';
  const getDescription = (descObj) => descObj?.[language] || descObj?.['en'] || '';

  // Split products by row
  const row1Products = PRODUCT_CATEGORIES.filter(p => p.row === 1);
  const row2Products = PRODUCT_CATEGORIES.filter(p => p.row === 2);

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="products-page">
      {/* Hero */}
      <section className="py-20 md:py-24 bg-slate-900 text-white" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4">
            {t('productsTitle')}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            {t('productsSubtitle')}
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12" data-testid="products-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Row 1 - 3 cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {row1Products.map((product) => (
              <div
                key={product.key}
                className="bg-white border border-slate-200 overflow-hidden card-lift"
                data-testid={`product-card-${product.key}`}
              >
                {/* Clickable image */}
                <div 
                  className="aspect-video bg-slate-100 overflow-hidden cursor-pointer relative"
                  onClick={() => handleViewDetails(product)}
                  data-testid={`product-image-${product.key}`}
                >
                  <img
                    src={product.image}
                    alt={getName(product.name)}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-2">
                    {getName(product.name)}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {getDescription(product.description)}
                  </p>
                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      onClick={() => handleViewDetails(product)}
                      className="rounded-none border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white uppercase tracking-wider text-xs font-bold"
                      data-testid={`product-details-${product.key}`}
                    >
                      {t('viewDetails')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Row 2 - 2 cards centered */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {row2Products.map((product) => (
              <div
                key={product.key}
                className="bg-white border border-slate-200 overflow-hidden card-lift"
                data-testid={`product-card-${product.key}`}
              >
                {/* Clickable image */}
                <div 
                  className="aspect-video bg-slate-100 overflow-hidden cursor-pointer relative"
                  onClick={() => handleViewDetails(product)}
                  data-testid={`product-image-${product.key}`}
                >
                  <img
                    src={product.image}
                    alt={getName(product.name)}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-2">
                    {getName(product.name)}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {getDescription(product.description)}
                  </p>
                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      onClick={() => handleViewDetails(product)}
                      className="rounded-none border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white uppercase tracking-wider text-xs font-bold"
                      data-testid={`product-details-${product.key}`}
                    >
                      {t('viewDetails')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Modal for Barras and Alambrón */}
      {showContactModal && contactProduct && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="product-contact-modal"
        >
          <div
            className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
              data-testid="modal-close-btn"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200">
              <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight pr-8">
                {getName(contactProduct.name)}
              </h2>
              <p className="text-slate-600 text-sm mt-2">
                {t('productInquirySubtitle') || 'Request information about this product'}
              </p>
            </div>

            {/* Form or Success Message */}
            <div className="p-6">
              {formSuccess ? (
                <div className="text-center py-8" data-testid="form-success-message">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    {t('thankYouTitle') || 'Thank you!'}
                  </h3>
                  <p className="text-slate-600">
                    {t('thankYouMessage') || 'Your message has been sent successfully. We will contact you soon.'}
                  </p>
                  <Button
                    onClick={closeModal}
                    className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold"
                    data-testid="close-success-btn"
                  >
                    {t('close')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="form-error">
                      {formError}
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('yourName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('company')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.company}
                      onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="contact-company-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('yourEmail')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Message with character counter */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('message')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      maxLength={MAX_MESSAGE_LENGTH}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors resize-none"
                      data-testid="contact-message-input"
                    />
                    <div className="text-right text-xs text-slate-500 mt-1" data-testid="char-counter">
                      {contactForm.message.length}/{MAX_MESSAGE_LENGTH}
                    </div>
                  </div>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold py-3"
                    data-testid="submit-inquiry-btn"
                  >
                    {formSubmitting ? (t('sending') || 'Sending...') : (t('sendMessage'))}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
